import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { api, lastActivity, markActivity } from "../api";
import { SESSION_KEY, useSession } from "../auth/session";
import { Dialog, icons } from "../company-ui";
import { HelpProvider } from "../help/HelpDrawer";
import { setCompanyScope, useCompanyScope } from "./company-scope";
import { HeaderSearch, type HeaderSearchHandle } from "./HeaderSearch";
import { NoticesBell } from "./NoticesBell";
import { rolesAnd, rolesOf } from "../admin/roles";
import { AppNav } from "./AppNav";
import { setTheme, useTheme, type Theme } from "./theme";
import { leaveShared } from "./leave";

/**
 * The app shell (ALN-25), from the mockup: 56px header (brand, search, help, company switcher, user
 * menu, and the notices bell from ALN-26), the menu (AppNav: the grouped rail and its collapse; a section row on phones) and the page area. Header search, page
 * help and the `/`, Ctrl/Cmd+K and `?` shortcuts are ALN-53.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { data } = useSession();
  const search = useRef<HeaderSearchHandle>(null);
  // `/` (outside a field) or Ctrl/Cmd+K focuses the header search from anywhere.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable]");
      if ((e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing && !e.ctrlKey && !e.metaKey && !e.altKey)) {
        e.preventDefault();
        search.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return (
    <HelpProvider>
      <a className="skiplink" href="#main">Skip to main content</a>
      {data?.platformAdmin && (
        <div className="labbar" role="note">
          <span className="labbar__tag">Support</span>
          <span>Avero support access to {data.tenant?.name}, read-only. Every page opened is in this winery's audit log.</span>
        </div>
      )}
      <header className="appheader">
        <Link href="/programs" className="brandmark" aria-label="Avero Vintner, Programs">
          {icons.glass}
          <b>Avero Vintner</b>
        </Link>
        <HeaderSearch ref={search} />
        <div className="appheader__r">
          <Link href="/help" className="iconbtn helptop" aria-label="Help center" title="Help center (press ? for help on this page)">{icons.help}</Link>
          <CompanySwitcher />
          {data?.tenant && <NoticesBell />}
          <UserMenu />
          {data?.shared && <button type="button" className="btn switchuser" onClick={() => void switchUser(sharedWho(data))}>{icons.swap}Switch user</button>}
        </div>
      </header>
      {data?.shared && data.idleMinutes ? <IdleWatch minutes={data.idleMinutes} who={sharedWho(data)} /> : null}
      <div className="shell">
        <AppNav />
        <main id="main" tabIndex={-1} className="shell__main">{children}</main>
      </div>
    </HelpProvider>
  );
}

function CompanySwitcher() {
  const { companyId, companies, tenantId } = useCompanyScope();
  if (!tenantId || companies.length === 0) return null;
  return (
    <>
      <label className="sr" htmlFor="company-scope">Company</label>
      <select id="company-scope" className="select select--hdr" value={companyId ?? ""} onChange={e => setCompanyScope(tenantId, e.target.value || null)}>
        {companies.length > 1 && <option value="">All companies</option>}
        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </>
  );
}

/**
 * Switch user and the idle sign-out (sign-in-methods.md B.2): end the session, then a full load of
 * /sign-in so nothing of the last person stays in memory, and replace the page in history so Back
 * doesn't show it. The server clears the browser's cache and storage for a shared session.
 */
type Who = { tenant?: string | null; user?: string | null } | null;
/** On a shared session, the person leaving (their rail choice is removed, TC-UI-008); on a personal computer, nobody. */
const sharedWho = (s: { shared?: boolean; tenant?: { id: string } | null; user?: { id: string } | null } | null | undefined): Who =>
  s?.shared ? { tenant: s.tenant?.id, user: s.user?.id } : null;
function endAndLeave(to: string, who: Who) {
  // shared: the server clears the browser even if it already ended the session for idleness
  return leaveShared(to, who, { signOut: () => api("/auth/sign-out", { body: { shared: true } }), replace: where => window.location.replace(where) });
}
const switchUser = (who: Who) => endAndLeave("/sign-in", who);

/** Whether the viewport is a phone's (the pill shows initials only at 640px and under). */
function useNarrow(): boolean {
  const query = "(max-width: 640px)";
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return narrow;
}

/**
 * A shared session signs out after `minutes` without use; "Still there?" shows 60 seconds before
 * (B.2). Use is a key, click or tap here; "I'm still here" tells the server too.
 */
function IdleWatch({ minutes, who }: { minutes: number; who: Who }) {
  const limit = minutes * 60_000;
  // the seconds left when the warning opened: the text is read once, not every second (B.2)
  const [seconds, setSeconds] = useState<number | null>(null);
  const leaving = useRef(false);
  useEffect(() => {
    const started = Date.now();
    const tick = () => {
      if (leaving.current) return;
      const idle = Date.now() - Math.max(lastActivity(), started);
      if (idle >= limit) { leaving.current = true; void endAndLeave(`/sign-in?idle=${minutes}`, who); return; }
      setSeconds(s => (idle >= limit - 60_000 ? s ?? Math.max(1, Math.ceil((limit - idle) / 1000)) : null));
    };
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [limit, minutes, who?.tenant, who?.user]); // eslint-disable-line react-hooks/exhaustive-deps
  const stay = () => { markActivity(); setSeconds(null); void api("/auth/session").catch(() => undefined); };
  return (
    <Dialog title="Still there?" open={seconds !== null} onClose={stay}
      actions={<div className="idle__acts"><button type="button" className="btn btn--primary btn--touch" data-autofocus onClick={stay}>I'm still here</button><button type="button" className="btn btn--touch" onClick={() => { leaving.current = true; void switchUser(who); }}>Sign out now</button></div>}>
      <p className="m0">To keep your account safe, you'll be signed out in {seconds} seconds so the next person can use this computer.</p>
    </Dialog>
  );
}

const THEMES: [Theme, string][] = [["light", "Light"], ["dark", "Dark"], ["system", "Match my system"]];
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join("") || "?";

/** The account menu: who you are, your account, Administration, theme, sign out. Arrow keys move. */
function UserMenu() {
  const { data } = useSession();
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const manage = Boolean(data?.permissions.includes("admin.manage"));
  const narrow = useNarrow();

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>(".menu__item")?.focus();
    const away = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const close = (refocus = true) => { setOpen(false); if (refocus) button.current?.focus(); };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (e.key === "Tab") { setOpen(false); return; }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const items = [...(menu.current?.querySelectorAll<HTMLElement>(".menu__item") ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    const n = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : (i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
    items[n]?.focus();
  };
  const go = (path: string) => { close(false); navigate(path); };
  const signOut = async () => {
    // a shared session leaves nothing behind: the same full reload as Switch user (B.2)
    if (data?.shared) { await switchUser(sharedWho(data)); return; }
    await api("/auth/sign-out", { body: {} });
    qc.removeQueries({ predicate: q => q.queryKey[0] !== SESSION_KEY[0] });
    qc.setQueryData(SESSION_KEY, null);
    navigate("/sign-in");
  };

  const name = data?.user?.name ?? "";
  // every role held here, in rank order (ALN-121): "Winemaker and Cellar"
  const role = data?.platformAdmin ? "Avero support" : rolesAnd(rolesOf(data ?? {}));
  return (
    <div className="um" ref={wrap}>
      {/* the pill (B.2): initials and full name; at 640px and under, initials only, the name stays its accessible name */}
      {/* the name starts the accessible name, so it holds the visible text (WCAG 2.5.3); the initials are drawn by CSS from data-initials */}
      <button ref={button} type="button" className="userpill" aria-label={`${name}, account menu`} aria-haspopup="menu" aria-expanded={open} aria-controls="user-menu" onClick={() => setOpen(!open)}>
        <span className="avatar avatar--initials" data-initials={initials(name)} aria-hidden="true" />
        {!narrow && <span className="userpill__name">{name}</span>}
      </button>
      {open && (
        <div className="menu" id="user-menu" role="menu" aria-label="Account" ref={menu} onKeyDown={onKey}>
          <div className="menu__head">
            <span className="avatar avatar--lg" aria-hidden="true">{initials(name)}</span>
            <div className="stack"><b>{name}</b><span className="small muted">{role} · {data?.tenant?.name}</span></div>
          </div>
          <div className="menu__sep" role="separator" />
          <button type="button" className="menu__item" role="menuitem" onClick={() => go("/account")}>Your account</button>
          {manage && <button type="button" className="menu__item" role="menuitem" onClick={() => go("/admin")}>Administration</button>}
          <button type="button" className="menu__item" role="menuitem" onClick={() => go("/help")}>Help center</button>
          <div className="menu__sep" role="separator" />
          <div className="menu__label" id="theme-label">Theme</div>
          <div role="group" aria-labelledby="theme-label">
            {THEMES.map(([k, l]) => (
              <button key={k} type="button" className="menu__item" role="menuitemradio" aria-checked={theme === k} onClick={() => setTheme(k)}>
                <span className="menu__dot" aria-hidden="true" />{l}
              </button>
            ))}
          </div>
          <div className="menu__sep" role="separator" />
          {!data?.shared && <button type="button" className="menu__item" role="menuitem" onClick={() => { close(false); void switchUser(sharedWho(data)); }}>Switch user</button>}
          <button type="button" className="menu__item" role="menuitem" onClick={() => void signOut()}>Sign out</button>
        </div>
      )}
    </div>
  );
}
